import { pathOr, filter, isNil, keys, pipe, unless } from 'ramda';

import { logout as logoutAPI } from '../../api';
import { clientDb, clientStorage, fbapp, globaldb, keyGen } from '../firebase';
import type { Store } from 'redux';
import { makeAudit, makeAuditForDevicesList, resetRememberMeIfExists, storePathAwaiterMaker } from '../helpers';
import { iConfig, iDefaultSettings, ItemType, iFullStoreState, UserAuth } from '../interfaces';
import { localStorage } from '../storage';

const awaitStorePath = <T>(
  store: Store<iFullStoreState>,
  pathArr: (keyof iFullStoreState | string)[],
  predicate: (v: T) => boolean = (v) => Boolean(v)
) =>
  new Promise<T>((resolve) => {
    const check = () => {
      const v = pathOr('missing', pathArr as any, store.getState()) as T | 'missing';
      if (v !== 'missing' && predicate(v as T)) {
        unsub();
        resolve(v as T);
      }
    };
    const unsub = store.subscribe(check);
    check();
  });

const reloadOncePerSession = (reason: 'version' | 'force') => {
  const flag = `reload-${reason}-done`;
  if (sessionStorage.getItem(flag)) return false;
  sessionStorage.setItem(flag, '1');
  window.location.reload();
  return true;
};

export const userTokenRefreshHandler = async (theStore: Store<iFullStoreState>) => {
  const user = await awaitStorePath<UserAuth>(
    theStore,
    ['auth', 'user'],
    (u) => !!u && !!u.beToken
  );
  if (!user?.beToken) return;

  const config = await awaitStorePath<iConfig>(
    theStore,
    ['general', 'config'],
    (c) => !!c && c.version != null && c.version !== ''
  );

  console.log('active-client', localStorage.get('active-client'));

  const versionRef = clientDb().child('config').child('version');
  const onVersion = (snap: any) => {
    const dbVersion = snap?.val?.();
    const appVersion = config?.version;
    if (dbVersion == null || dbVersion === '' || appVersion == null || appVersion === '') return;
    if (String(dbVersion) !== String(appVersion)) {
      try {
        (localStorage as any).forget?.('db-config');
      } catch {
        try {
          localStorage.forget('db-config');
        } catch {}
      }
      reloadOncePerSession('version');
    }
  };
  versionRef.on('value', onVersion);

  const forceRef = clientDb()
    .child('people')
    .child('people-details')
    .child(user.uid)
    .child('force-refresh');
  const onForce = async (snap: any) => {
    const mustRefresh = Boolean(snap?.val?.());
    if (!mustRefresh) return;
    try {
      await forceRef.remove();
    } catch {}
    if (user.beToken.role !== 'superAdmin') {
      reloadOncePerSession('force');
    }
  };
  forceRef.on('value', onForce);

  return () => {
    try { versionRef.off('value', onVersion); } catch {}
    try { forceRef.off('value', onForce); } catch {}
  };
};

export const getDefaultSettings = async () => (await clientDb().child('default-settings').once('value')).val() || {} as iDefaultSettings

export const getDbConfig = async () => (await clientDb().child('config').once('value')).val() || {} as iConfig;

export const watchLabels = (callback) => clientDb().child('labels').on('value', (res) => {
    callback(res.val());
}, (err) => console.log(err));

export const addTripLabel = (user: UserAuth) => async (newLabel) => clientDb().update(makeAudit(user, {
    [`labels/trip-labels/${newLabel}`]: newLabel
}));

export const addScheduleLabel = (user: UserAuth) => async (newLabel) => clientDb().update(makeAudit(user, {
    [`labels/schedule-labels/${newLabel}`]: newLabel
}));

export const incidentIcons = async () => (await globaldb.child('incident-icons').once('value')).val();

export const saveIcon = async (file: File) => {
    const key = keyGen();
    await clientStorage().child(`icons/${key}/file`).put(file);

    return await clientStorage().child(`icons/${key}/file`).getDownloadURL();
};

export const getPersonAllowedSee = async (personId: string) => {
    return (await clientDb().child('acl').child('items-allowed').child(personId).once('value')).val() || {};
};

export const watchPersonAllowedSee = (personId: string, callback) => {
    const wrapper = (x) => callback(x.val() || {});

    return clientDb().child('acl').child('items-allowed').child(personId).on('value', unless(isNil, wrapper), (err) => console.log(err));
};

export const getDeviceAllowedSee = async (deviceId: string, deviceTags: string[]) => {
    const items = (await clientDb().child('acl').child('items-allowed').once('value')).val() || {};

    return pipe(
      filter(({device = {}, tags = {}}) => device[deviceId] || deviceTags.some((t) => tags[t])),
      keys,
    )(items);
};

export const getEventTypes = async () => (await globaldb.child('events').once('value')).val();

export const setAllowedSee = (user: UserAuth) => async (personId: string, type: ItemType, itemId: string) => {
    if (type === ItemType.device) {
        return clientDb().update(makeAudit(user, {
            [`/acl/items-allowed/${personId}/device/${itemId}/${itemId}`]:'device',
            [`/acl/can/see-fences/${personId}`]: true
        }));
    }

    const taggedDevices = (await clientDb().child('tags').child(itemId).child('instances').child('device').once('value')).val() || {};

    const update = {};

    update[`/tags/${itemId}/instances/allowed-see/person/${personId}`] = true;
    update[`/acl/can/see-fences/${personId}`] = true;
    console.log('taggedDevices', taggedDevices);
    Object.keys(taggedDevices).forEach((deviceId) => {
        update[`/acl/items-allowed/${personId}/device/${deviceId}/${itemId}`] = 'tag'
        update[`/acl/items-allowed/${personId}/device/${deviceId}/readOnly`] = true
    });

    const updateForDevicesList = {};

    updateForDevicesList[
      `/tags/${itemId}/details/devices-list`
    ] = {personId: personId, action: 'tag was ADDED to the person'};

    clientDb().update(makeAuditForDevicesList(user, updateForDevicesList));

    await clientDb().update(makeAudit(user, update));

};
export const readOnlyAccessToggle= (user: UserAuth) => async (personId: string,  itemId: string, value: boolean ) => {
    await clientDb().update(makeAudit(user, {
        [`/acl/items-allowed/${personId}/device/${itemId}/readOnly`]: value
    }));

    if (value) {
        await clientDb().update(makeAudit(user, {
            [`/acl/can/see-fences/${personId}`]: true
        }));
        await clientDb().update(makeAudit(user, {
            [`/acl/can/edit-fences/${personId}`]: true
        }));
    }
    else {
        await clientDb().update(makeAudit(user, {
            [`/acl/can/see-fences/${personId}`]: false
        }));

        await clientDb().update(makeAudit(user, {
            [`/acl/can/edit-fences/${personId}`]: false
        }));
    }

    // const isReadOnly = (await (clientDb().child(`/acl/items-allowed/${personId}/device/${itemId}/readOnly`).once('value'))).val();
    //
    // if (isReadOnly) {
    //     await clientDb().update(makeAudit(user, {
    //         [`/acl/can/see-fences/${personId}`]: false
    //     }));
    //
    //     await clientDb().update(makeAudit(user, {
    //         [`/acl/can/edit-fences/${personId}`]: false
    //     }));
    // }
    // else {
    //     await clientDb().update(makeAudit(user, {
    //         [`/acl/can/see-fences/${personId}`]: true
    //     }));
    //     await clientDb().update(makeAudit(user, {
    //         [`/acl/can/edit-fences/${personId}`]: true
    //     }));
    // }
};
export const tagReadOnlyToggle= (user: UserAuth) => async (tagId: string,  personId: string, value: boolean ) => {
    await clientDb().update(makeAudit(user, {
        [`/tags/${tagId}/instances/allowed-see/readOnly/${personId}`]: value
    }));
};

export const getReadOnlyStatus = async (personId: string, itemId: string) => {
    const responce = (await (clientDb().child(`/acl/items-allowed/${personId}/device/${itemId}/readOnly`).once('value'))).val()
    return !!responce;
};

// returnі true if status is read only
export const getTagReadOnlyStatus = async (personId: string, tagId: string) => {
    try {
        return (await (clientDb().child(`/tags/${tagId}/instances/allowed-see/readOnly/${personId}`).once('value'))).val() || false;
    } catch (error) {
        if (localStorage.get('active-client', 'non-empty-placeholder') === "non-empty-placeholder") {
            logoutAPI().finally(() => {
                resetRememberMeIfExists();
                document.location.href = '/';
                fbapp.auth().signOut();
            });
        }
    }
};

export const removeAllowedSee = (user: UserAuth) => async (personId: string, type: ItemType, itemId: string) => {

    const info = (await clientDb().child('acl').child('items-allowed').child(personId).child('device').child(itemId).once('value')).val() || {};

    if (type == ItemType.device) {
        const sourceKeys = Object.keys(info || {}).filter(sourceId => sourceId !== 'readOnly');
        const hasDeviceSource = sourceKeys.includes(itemId);
        const hasOtherSources = sourceKeys.some(sourceId => sourceId !== itemId);

        if (!hasDeviceSource) {
            return clientDb().update(makeAudit(user, {
                [`/acl/can/see-fences/${personId}`]: false
            }));
        }

        return clientDb().update(makeAudit(user, {
            [`acl/items-allowed/${personId}/device/${itemId}/${itemId}`]: null,
            ...(!hasOtherSources && {[`acl/items-allowed/${personId}/device/${itemId}/readOnly`]: null}),
            [`/acl/can/see-fences/${personId}`]: false
        }));
    }

    const taggedDevices = (await clientDb().child('tags').child(itemId).child('instances').child('device').once('value')).val() || {};

    const update = {};

    update[`/tags/${itemId}/instances/allowed-see/person/${personId}`] = null;
    update[`/tags/${itemId}/instances/allowed-see/readOnly/${personId}`] = null;
    update[`/acl/can/see-fences/${personId}`] = false;

    for (const deviceId of Object.keys(taggedDevices)) {
        const value = (await clientDb().child(`acl/items-allowed/${personId}/device/${deviceId}`).once('value')).val() || {};
        const sourceKeys = Object.keys(value).filter(sourceId => sourceId !== 'readOnly');
        const hasCurrentTagSource = sourceKeys.includes(itemId);
        const hasOtherSources = sourceKeys.some(sourceId => sourceId !== itemId);

        if (!hasCurrentTagSource) {
            continue;
        }

        const updatePath = hasOtherSources
            ? `/acl/items-allowed/${personId}/device/${deviceId}/${itemId}`
            : `/acl/items-allowed/${personId}/device/${deviceId}`;

        update[updatePath] = null;
    }

    const updateForDevicesList = {};

    updateForDevicesList[
      `/tags/${itemId}/details/devices-list`
    ] = {personId: personId, action: 'tag was REMOVED for the person'};

    clientDb().update(makeAuditForDevicesList(user, updateForDevicesList));

    await clientDb().update(makeAudit(user, update))
}

export const watchPersonCan = (uid, callback) => {
    const userCanRef = clientDb().child('acl').child('can');
    userCanRef.on("value", (res) => {
        const val = Object.entries(res.val()).filter(([permission, x]) => x[uid]).map(([permission]) => permission);
        callback(val);
    }, err => console.log(err))
    return () => userCanRef.off("value");
}

export enum DeviceInfoSectionOrderItem {
    LAST_PING = 'last-ping',
    TAGS = 'tags',
    EXTRA_INFO = 'extra-info',
    VISIBLE_TO = 'visible-to',
    ASSIGNED_DRIVER = 'assigned-driver',
    // NOT DRIVER SECTIONS
    ALERT = 'alert',
    ENGINE_HISTORY = 'engine-history',
    NOTES = 'notes',
}

export const DeviceInfoHidden = {
    ...DeviceInfoSectionOrderItem,
    LAST_PING: 'last-ping-details',
} as const;

export type DeviceInfoHidden = typeof DeviceInfoHidden[keyof typeof DeviceInfoHidden];

export const defaultDeviceInfoSectionOrderList: DeviceInfoSectionOrderItem[] = [
    DeviceInfoSectionOrderItem.LAST_PING,
    DeviceInfoSectionOrderItem.TAGS,
    DeviceInfoSectionOrderItem.EXTRA_INFO,
    DeviceInfoSectionOrderItem.VISIBLE_TO,
    DeviceInfoSectionOrderItem.ASSIGNED_DRIVER,
    DeviceInfoSectionOrderItem.ALERT,
    DeviceInfoSectionOrderItem.ENGINE_HISTORY,
    DeviceInfoSectionOrderItem.NOTES,
];

const getDeviceInfoSectionOrder = async (): Promise<DeviceInfoSectionOrderItem[]> => (await clientDb().child('default-settings/device-info-section-order').once('value')).val() || [];

export const getDeviceInfoSectionFullOrder = async (): Promise<DeviceInfoSectionOrderItem[]> => {
    const savedOrder = await getDeviceInfoSectionOrder();
    // add all items + remove duplicates
    return [...new Set([...savedOrder, ...defaultDeviceInfoSectionOrderList])];
}

export const setDeviceInfoSectionOrder = (user: UserAuth) => async (newOrder: DeviceInfoSectionOrderItem[]) => (
  clientDb().update(makeAudit(user, {
      [`default-settings/device-info-section-order`]: newOrder,
  }))
);

export const formatSectionKey = (key: string) =>
    key.toLowerCase().replace(/ /g, '-');

export const getDeviceInfoSectionHiddenMap = async (
    items: { name: string }[]
): Promise<{ [key: string]: boolean }> => {
    const result: { [key: string]: boolean } = {};

    await Promise.all(
        items.map(async ({ name }) => {
            const formattedName = formatSectionKey(name);
            const snapshot = await clientDb()
                .child(`/default-settings/device-info-section-hidden/${formattedName}/isHidden`)
                .once('value');

            result[name] = snapshot.exists() ? snapshot.val() : true;
        })
    );

    return result;
}

export type DeviceInfoSectionVisibilityMap = Record<string, boolean>;

export const getDeviceInfoHiddenSettings = async (): Promise<DeviceInfoSectionVisibilityMap> => {
    try {
        const snapshot = await clientDb()
            .child(`/default-settings/device-info-section-hidden`)
            .once("value");

        const data = snapshot.val() || {};

        const visibilityMap: DeviceInfoSectionVisibilityMap = {};

        for (const sectionName in data) {
            visibilityMap[sectionName] = data[sectionName]?.isHidden === true;
        }

        return visibilityMap;
    } catch (error) {
        throw error;
    }
};

export const setDeviceInfoHidden = async (deviceName: string, value: boolean) => {
    try {
        const formattedName = formatSectionKey(deviceName);
        await clientDb()
            .child(`/default-settings/device-info-section-hidden/${formattedName}/isHidden`)
            .set(value);
    } catch (error) {
        throw error;
    }
};
